import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Tag, TagGroup, Tooltip } from '@heroui/react';
import { getNodeTestIdProp } from '@/utils';
import type { ProductFilterGroup } from '../productListFilters';
import {
    describeAppliedFilterTag,
    summarizeTagValues,
    toFilterGroups,
    type AppliedFilterTag,
} from './productFiltersMeta';
import {
    joinFilterValues,
    resolveFilterWords,
    type FilterWords,
} from './filterLabels';

type AppliedFilterTagsProps = {
    tags: AppliedFilterTag[];
    onRemove: (group: ProductFilterGroup) => void;
    onClearAll: () => void;
};

/**
 * What one tag reads, and its whole text when that is shortened: the hover
 * text, and the tag's name for assistive tech either way.
 */
type TagText = { label: string; full: string; isShortened: boolean };

/**
 * The applied filters under the toolbar (BOS-199): one tag per group, whose ×
 * clears that group at once, then Clear all. A tag listing more values than it
 * spells out reads "A, B +3" and shows the whole list on hover.
 *
 * From `sm` up the row wraps; on phones it stays one line that scrolls
 * sideways, so it never pushes the grid down by more than a line.
 */
export const AppliedFilterTags: React.FC<AppliedFilterTagsProps> = ({
    tags,
    onRemove,
    onClearAll,
}) => {
    const { t, i18n } = useTranslation('pages');
    const wordsOf = (words: FilterWords): string =>
        resolveFilterWords(words, (key, values) => t(key, values));
    const language = i18n?.resolvedLanguage ?? i18n?.language ?? 'en';

    const withValue = (name: string, value: string) =>
        t('products.components.productTable.filters.tagText', { name, value });

    /**
     * "Brand: A, B +3", the whole list on hover; "Price: ₾ 10.00 – ₾ 50.00";
     * the name alone for a switch.
     */
    const textOf = (tag: AppliedFilterTag): TagText => {
        const described = describeAppliedFilterTag(tag);
        const name = t(described.nameKey);
        if (described.values.length === 0) {
            return { label: name, full: name, isShortened: false };
        }
        const values = described.values.map(wordsOf);
        const { shown, hiddenCount } = summarizeTagValues(values);
        const more =
            hiddenCount > 0
                ? ` ${t('products.components.productTable.filters.more', { count: hiddenCount })}`
                : '';
        return {
            label: withValue(
                name,
                `${joinFilterValues(shown, language)}${more}`,
            ),
            full: withValue(name, joinFilterValues(values, language)),
            isShortened: hiddenCount > 0,
        };
    };

    if (tags.length === 0) {
        return null;
    }

    return (
        <div
            className="flex items-center gap-2 overflow-x-auto sm:flex-wrap sm:overflow-x-visible"
            {...getNodeTestIdProp('product-table-applied-filters')}
        >
            <TagGroup
                aria-label={t(
                    'products.components.productTable.filters.applied',
                )}
                onRemove={(keys) =>
                    toFilterGroups(keys).forEach((group) => onRemove(group))
                }
                size="sm"
            >
                <TagGroup.List className="flex-nowrap sm:flex-wrap">
                    {tags.map((tag) => {
                        const text = textOf(tag);
                        return (
                            <Tag
                                className="shrink-0"
                                id={tag.group}
                                key={tag.group}
                                textValue={text.full}
                                {...getNodeTestIdProp(
                                    `product-table-filter-tag-${tag.group}`,
                                )}
                            >
                                {text.isShortened ? (
                                    <Tooltip delay={300}>
                                        <Tooltip.Trigger>
                                            {text.label}
                                        </Tooltip.Trigger>
                                        <Tooltip.Content>
                                            <p>{text.full}</p>
                                        </Tooltip.Content>
                                    </Tooltip>
                                ) : (
                                    text.label
                                )}
                            </Tag>
                        );
                    })}
                </TagGroup.List>
            </TagGroup>
            <Button
                className="shrink-0"
                onPress={onClearAll}
                size="sm"
                variant="ghost"
                {...getNodeTestIdProp('product-table-filters-clear-all')}
            >
                {t('products.components.productTable.filters.clearAll')}
            </Button>
        </div>
    );
};
